/**
 * Initiative Preview Pane — Slide-over panel showing initiative details.
 *
 * When the recommendation carries an `initiative_id` (i.e. it was matched to
 * a real ProjectInitiative), we fetch the full initiative data via the
 * existing `useInitiative` hook so ALL fields (KPIs, skills, phases …)
 * are available. For template-based recommendations (no initiative_id),
 * we fall back to whatever the recommendation object already contains.
 */

import { Button } from '@/components/ui/button'
import { X, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { InitiativeDetailContent, InitiativeBadgeRow } from './initiative-detail-content'
import { useInitiative } from '@/features/project-initiatives/hooks'

interface InitiativePreviewPaneProps {
    initiative: any | null
    onClose: () => void
}

export function InitiativePreviewPane({ initiative, onClose }: InitiativePreviewPaneProps) {
    return (
        <AnimatePresence>
            {initiative && (
                <InitiativePreviewContent initiative={initiative} onClose={onClose} />
            )}
        </AnimatePresence>
    )
}

function InitiativePreviewContent({ initiative, onClose }: { initiative: any; onClose: () => void }) {
    // If the recommendation was matched to a real ProjectInitiative, fetch full data
    const initiativeId = initiative.initiative_id || ''
    const { data: fullInitiative, isLoading } = useInitiative(initiativeId)

    // Merge: prefer full initiative data, fall back to recommendation data
    const data = fullInitiative ? { ...initiative, ...fullInitiative } : initiative

    return (
        <>
            {/* Backdrop */}
            <motion.div
                className="fixed inset-0 bg-black/30 z-40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            />

            {/* Panel */}
            <motion.div
                className="fixed top-0 right-0 h-full w-full max-w-6xl bg-white shadow-2xl z-50 flex flex-col"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            >
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b shrink-0">
                    <div className="flex-1 min-w-0 mr-4">
                        <h2 className="text-xl font-bold text-gray-900 leading-tight">{data.title || data.name}</h2>
                        {data.short_description && (
                            <p className="text-sm text-gray-600 mt-1">{data.short_description}</p>
                        )}
                        <div className="mt-3">
                            <InitiativeBadgeRow data={data} />
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {isLoading && initiativeId ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            <span className="ml-2 text-sm text-muted-foreground">Loading initiative details...</span>
                        </div>
                    ) : (
                        <InitiativeDetailContent data={data} />
                    )}
                </div>
            </motion.div>
        </>
    )
}
